import { type ApiSchemas, call, newIdempotencyKey } from '@rental/contracts/client'
import type { RefundMethod } from '@rental/domain'
import { format, formatDateTime, formatEuro } from '@rental/i18n'
import { Button, Field, Notice } from '@rental/ui'
import { useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useEffect, useId, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useAccess } from '../access-context'
import { api } from '../api'
import { canDo } from '../booking-can'
import { DrawerField } from '../cash/DrawerField'
import { rememberedPoint } from '../cash/drawer'
import { refreshDrawersAfter } from '../cash/queries'
import { ChoiceChips } from '../components/ChoiceChips'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { type ChipTone, StatusChip } from '../components/StatusChip'
import { ReasonList, SubPanel } from '../components/SubPanel'
import { TechnicalLines } from '../components/TechnicalDetails'
import { FIELD, FIELD_ROW, ProblemNotice, StaffInput } from '../components/ui'
import { MoneyDocuments, refreshFiscal } from '../fiscal/Documents'
import {
  type DocumentDraft,
  defaultKind,
  documentBody,
  newDraft,
  withKind,
} from '../fiscal/document'
import { FiscalFields } from '../fiscal/FiscalFields'
import { useLocale } from '../i18n'
import { centsForInput, parseEuroCents } from '../money'
import type { StaffProblem } from '../problems'
import { refreshAfterChange } from '../refresh'

type Detail = ApiSchemas['StaffBookingDetail']
type Refund = Detail['refunds'][number]
type Payment = Detail['payments'][number]

/** A refund through Stripe is asked again this often, this many times, until Stripe answers. */
const POLL_EVERY_MS = 2_000
const POLLS = 15

const STATE_TONE: Record<Refund['status'], ChipTone> = {
  pending: 'info',
  requires_action: 'warning',
  succeeded: 'success',
  failed: 'danger',
  canceled: 'danger',
}

/** Ended by us, never by Stripe (server: refunds.ts LOCAL_ENDINGS). */
const LOCAL_ENDINGS: readonly string[] = ['superseded', 'not_sent']

/**
 * One refund's tone, as Today counts it (refunds/views.ts): one that Stripe
 * called off did not go through, red; one we ended ourselves has simply
 * ended, neutral (DESIGN.md, StatusChip).
 */
function refundTone(refund: Detail['refunds'][number]): ChipTone {
  if (refund.status === 'canceled' && LOCAL_ENDINGS.includes(refund.failureReason ?? ''))
    return 'neutral'
  return STATE_TONE[refund.status]
}

/** What the form asked to give back, held while the question is open (D11). */
type RefundRequest = {
  body: ApiSchemas['RefundRequest']
  payment: Payment
}

/**
 * PAY-3 (S3), on the booking: the refunds made, and, while the customer is
 * owed money back, the form to give it back (the Owner, Q-75) or where the
 * others find it (Today's "Da restituire"). A refund through Stripe is shown
 * as Stripe moves it on: the screen asks again for a while after it leaves.
 */
export function RefundPanel({ detail }: { detail: Detail }) {
  const { locale, messages: m } = useLocale()
  const { takeOver } = useAccess()
  const queryClient = useQueryClient()
  const code = detail.booking.code
  const may = canDo(detail.can)
  const mayRefund = may('refund')
  const creditCents = Math.max(0, -detail.money.balanceCents)
  const [notice, setNotice] = useState<'done' | 'sent' | 'synced' | null>(null)
  const [polls, setPolls] = useState(0)
  // D11: the refund is said back and asked once more. The question is the panel's: the
  // form starts again whenever what is owed changes (a refusal can change it), and the
  // answer, a refusal included, and the key a retry replays must outlive it.
  const dialog = useRef<HTMLDialogElement>(null)
  const [asking, setAsking] = useState<RefundRequest | null>(null)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<StaffProblem | null>(null)
  // A refusal said in the question stays in sight once it closes, and is not said again. One
  // that comes after the question closed (Chromium closes a busy one on a second Esc) is said.
  const [problemSaid, setProblemSaid] = useState(false)
  const lastSent = useRef<{ body: string; key: string } | null>(null)

  function ask(request: RefundRequest) {
    // Drawn before it opens, so the question has its name and words when the focus lands in it.
    flushSync(() => {
      setProblem(null)
      setProblemSaid(false)
      setNotice(null)
      setAsking(request)
      setOpen(true)
    })
    dialog.current?.showModal()
  }

  async function confirm() {
    if (!asking) return
    setProblem(null)
    setProblemSaid(false)
    // A retry of the same refund replays; a different one gets its own key.
    const serialized = JSON.stringify(asking.body)
    if (lastSent.current?.body !== serialized) {
      lastSent.current = { body: serialized, key: newIdempotencyKey() }
    }
    setBusy(true)
    const result = await call(
      api.POST('/v1/staff/bookings/{code}/refunds', {
        params: { path: { code }, header: { 'idempotency-key': lastSent.current.key } },
        body: asking.body,
      }),
    )
    setBusy(false)
    if (result.ok) {
      lastSent.current = null
      dialog.current?.close()
      setNotice(asking.body.method === 'stripe' ? 'sent' : 'done')
      setPolls(0)
      // Today's "Da restituire" too, and Fiscale's list.
      await refreshAfterChange(queryClient, code)
      await refreshFiscal(queryClient)
      return
    }
    if (takeOver(result.problem)) return
    setProblem(result.problem)
    // Whatever moved meanwhile (another refund, a payment, the drawer), show it as it is now.
    await queryClient.invalidateQueries({ queryKey: ['booking', code] })
    await refreshDrawersAfter(queryClient, result.problem)
  }
  const inFlight = detail.refunds.some(
    (refund) => refund.method === 'stripe' && refund.status === 'pending',
  )

  useEffect(() => {
    if (!inFlight || polls >= POLLS) return
    const timer = window.setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: ['booking', code] })
      setPolls((count) => count + 1)
    }, POLL_EVERY_MS)
    return () => window.clearTimeout(timer)
  }, [inFlight, polls, code, queryClient])

  const viaStripe =
    detail.payments.some((payment) => payment.refundMethods.includes('stripe')) ||
    detail.refunds.some((refund) => refund.method === 'stripe')

  return (
    <>
      {detail.refunds.length > 0 ? (
        <RefundList
          refunds={detail.refunds}
          documents={detail.fiscalDocuments}
          code={code}
          mayType={may('fiscalDocument')}
        />
      ) : null}
      {/* Asked again, the button stays where it was pressed: the answer is said to it. A refund
          closes the question and starts the form again, so its answer takes the focus. */}
      {notice === 'synced' ? (
        <Notice tone="success" live="polite">
          <p>{m.refund.synced}</p>
        </Notice>
      ) : notice ? (
        <OutcomeNotice key={notice}>
          <p>{m.refund[notice]}</p>
        </OutcomeNotice>
      ) : null}
      {creditCents > 0 && mayRefund ? (
        <RefundForm
          // A new account (a refund made, a payment found) starts the form again from it.
          key={creditCents}
          detail={detail}
          creditCents={creditCents}
          busy={busy}
          // While the question is open its answer shows in it; closed, by the form.
          problem={open ? null : problem}
          problemSaid={problemSaid}
          onAsk={ask}
        />
      ) : open ? null : (
        <ProblemNotice problem={problem} quiet={problemSaid} />
      )}
      {creditCents > 0 && !mayRefund ? (
        <p className="text-sm text-text-muted">{m.refund.ownerOnly}</p>
      ) : null}
      {mayRefund && viaStripe ? (
        <SyncWithStripe code={code} onDone={() => setNotice('synced')} />
      ) : null}
      {mayRefund ? (
        <ConfirmDialog
          dialogRef={dialog}
          // Asked nothing, it holds nothing: no hidden copy of a question already answered.
          title={
            asking
              ? format(m.refund.confirm.title, {
                  amount: formatEuro(asking.body.amountCents, locale),
                })
              : null
          }
          confirmLabel={
            asking
              ? format(m.refund.confirm.confirm, {
                  amount: formatEuro(asking.body.amountCents, locale),
                })
              : ''
          }
          cancelLabel={m.lifecycle.back}
          busy={busy}
          onConfirm={() => void confirm()}
          onClose={() => {
            setProblemSaid(problem !== null)
            setOpen(false)
            setAsking(null)
          }}
        >
          {asking ? <RefundSummary request={asking} /> : null}
          {open ? <ProblemNotice problem={problem} /> : null}
        </ConfirmDialog>
      ) : null}
    </>
  )
}

/** D11: the refund said back in the form's own words: how, from which payment, why. */
function RefundSummary({ request }: { request: RefundRequest }) {
  const { locale, messages: m } = useLocale()
  const { body, payment } = request
  return (
    <div className="flex flex-col gap-3">
      <dl className="flex flex-col gap-2">
        <div className="flex flex-col">
          <dt className="text-sm text-text-muted">{m.refund.method}</dt>
          <dd>{m.refund.methods[body.method]}</dd>
        </div>
        <div className="flex flex-col">
          <dt className="text-sm text-text-muted">{m.refund.payment}</dt>
          <dd className="figures">
            {format(m.refund.line, {
              amount: formatEuro(payment.amountCents, locale),
              method: m.paymentMethods[payment.method],
              date: formatDateTime(locale, payment.createdAt),
            })}
          </dd>
        </div>
        <div className="flex flex-col">
          <dt className="text-sm text-text-muted">{m.refund.reason}</dt>
          <dd className="whitespace-pre-line wrap-anywhere">{body.reason}</dd>
        </div>
      </dl>
      <p className="font-medium">{m.refund.confirm.final}</p>
    </div>
  )
}

function RefundList({
  refunds,
  documents,
  code,
  mayType,
}: {
  refunds: readonly Refund[]
  documents: Detail['fiscalDocuments']
  /** S9: the booking's answer (fiscalDocument). */
  mayType: boolean
  code: string
}) {
  const { locale, messages: m } = useLocale()
  return (
    <>
      <h3 className="text-lg">{m.refund.list}</h3>
      <ul className="flex flex-col gap-2">
        {refunds.map((refund) => (
          <li key={refund.id} className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="figures">
                {format(m.refund.line, {
                  amount: formatEuro(refund.amountCents, locale),
                  method: m.refund.methods[refund.method],
                  date: formatDateTime(locale, refund.createdAt),
                })}
                {refund.reference ? ` · ${refund.reference}` : ''}
              </span>
              <span className="text-sm text-text-muted">
                {refund.recordedBy
                  ? format(m.refund.by, { name: refund.recordedBy })
                  : m.refund.byStripe}
              </span>
              <StatusChip tone={refundTone(refund)}>{m.refund.states[refund.status]}</StatusChip>
            </div>
            {refund.failureReason === 'superseded' ? (
              <p className="text-sm text-text-muted">{m.refund.superseded}</p>
            ) : refund.failureReason === 'not_sent' ? (
              <p className="text-sm text-text-muted">{m.refund.notSent}</p>
            ) : null}
            {/* Its state says what happened and what to do; Stripe's own reason is the Owner's (C5). */}
            <TechnicalLines
              lines={
                refund.failureReason && !LOCAL_ENDINGS.includes(refund.failureReason)
                  ? [format(m.app.stripeCode, { code: refund.failureReason })]
                  : []
              }
            />
            {refund.reason ? (
              <p className="whitespace-pre-line text-sm text-text-muted">{refund.reason}</p>
            ) : null}
            {/* S6 (CMP-3): money that went back has its return or credit note. */}
            {refund.status === 'succeeded' ? (
              <MoneyDocuments
                documents={documents}
                here={refund.fiscalDocumentHere}
                mayType={mayType}
                target={{
                  money: 'refund',
                  id: refund.id,
                  method: refund.method,
                  amountCents: refund.amountCents,
                  at: refund.succeededAt ?? refund.createdAt,
                  code,
                }}
              />
            ) : null}
          </li>
        ))}
      </ul>
    </>
  )
}

/**
 * The Owner gives money back against one payment, by one of the ways it may
 * go (refundMethodsFor): never more than is owed back, nor than is left of
 * that payment. The server decides again; this only offers what it allows.
 * The form gathers the answer and says what is missing; the panel says it
 * back, asks once more and sends it (D11).
 */
function RefundForm({
  detail,
  creditCents,
  busy,
  problem,
  problemSaid,
  onAsk,
}: {
  detail: Detail
  creditCents: number
  busy: boolean
  problem: StaffProblem | null
  /** Said in the question already: here it stays in sight, not said again. */
  problemSaid: boolean
  onAsk: (request: RefundRequest) => void
}) {
  const { locale, messages: m } = useLocale()
  const code = detail.booking.code
  const payments = detail.payments.filter(
    (payment) => payment.refundableCents > 0 && payment.refundMethods.length > 0,
  )
  const onlyPayment = payments.length === 1 ? payments[0] : undefined
  const [paymentId, setPaymentId] = useState(onlyPayment?.id ?? '')
  const payment = payments.find((item) => item.id === paymentId)
  const [method, setMethod] = useState<RefundMethod | ''>(
    onlyPayment?.refundMethods.length === 1 ? (onlyPayment.refundMethods[0] ?? '') : '',
  )
  const limitOf = (item: Payment | undefined) =>
    item ? Math.min(creditCents, item.refundableCents) : creditCents
  const [amount, setAmount] = useState(() =>
    onlyPayment ? centsForInput(limitOf(onlyPayment), locale) : '',
  )
  const [reason, setReason] = useState('')
  const [reference, setReference] = useState('')
  // S5: cash leaves an open drawer.
  const [cashSessionId, setCashSessionId] = useState<string | null>(null)
  // S6 (CMP-3): the return or credit note, typed with a refund by hand.
  const [fiscalDraft, setFiscalDraft] = useState<DocumentDraft>(() =>
    newDraft('refund', method, { point: rememberedPoint() }),
  )
  const [documentMissing, setDocumentMissing] = useState<'point' | 'printed' | null>(null)
  const reasonsId = useId()

  if (payments.length === 0) {
    return (
      <>
        <p className="text-sm text-text-muted">{m.refund.noPayment}</p>
        <ProblemNotice problem={problem} quiet={problemSaid} />
      </>
    )
  }

  const limit = limitOf(payment)
  const cents = parseEuroCents(amount)
  const amountOk = cents !== null && cents >= 1 && cents <= limit
  const byHand = method !== '' && method !== 'stripe'
  // UI-1c: what still stops the refund, in words; none left means it can be asked.
  const why = m.refund.why
  const reasons = [
    ...(payment === undefined ? [why.payment] : []),
    ...(payment !== undefined && method === '' ? [why.method] : []),
    ...(cents === null || cents < 1 ? [why.amount] : []),
    ...(cents !== null && cents > limit
      ? [format(why.overMax, { amount: formatEuro(limit, locale) })]
      : []),
    ...(reason.trim().length < 3 ? [why.reason] : []),
    ...(method === 'cash' && cashSessionId === null ? [why.drawer] : []),
  ]
  const ready = reasons.length === 0

  function chooseMethod(next: RefundMethod | '') {
    setMethod(next)
    // Until a number is typed, the kind follows the method (a transfer gets a credit note).
    setFiscalDraft((draft) =>
      draft.number.trim() ? draft : withKind(draft, defaultKind('refund', next)),
    )
  }

  function choosePayment(next: Payment) {
    setPaymentId(next.id)
    chooseMethod(next.refundMethods.length === 1 ? (next.refundMethods[0] ?? '') : '')
    setAmount(centsForInput(limitOf(next), locale))
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!ready || !payment || method === '' || cents === null) return
    // Through Stripe the money goes back later: its document is typed from Fiscale then.
    const fiscal = byHand
      ? documentBody(fiscalDraft, { pointFromDrawer: method === 'cash' })
      : ({ ok: true, document: null } as const)
    if (!fiscal.ok) {
      setDocumentMissing(fiscal.missing)
      return
    }
    setDocumentMissing(null)
    onAsk({
      payment,
      body: {
        paymentId: payment.id,
        method,
        amountCents: cents,
        reason: reason.trim(),
        ...(byHand && reference.trim() ? { reference: reference.trim() } : {}),
        ...(method === 'cash' && cashSessionId ? { cashSessionId } : {}),
        ...(fiscal.document ? { fiscalDocument: fiscal.document } : {}),
      },
    })
  }

  return (
    <SubPanel title={m.refund.title}>
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <p>{format(m.refund.intro, { amount: formatEuro(creditCents, locale) })}</p>
        <ChoiceChips
          name="refund-payment"
          label={m.refund.payment}
          options={payments.map((item) => ({
            value: item.id,
            label: (
              <span className="figures">
                {format(m.refund.paymentOption, {
                  amount: formatEuro(item.amountCents, locale),
                  method: m.paymentMethods[item.method],
                  date: formatDateTime(locale, item.createdAt),
                  left: formatEuro(item.refundableCents, locale),
                })}
              </span>
            ),
          }))}
          value={paymentId}
          onChange={(id) => {
            const item = payments.find((candidate) => candidate.id === id)
            if (item) choosePayment(item)
          }}
        />
        {payment ? (
          <ChoiceChips
            name="refund-method"
            label={m.refund.method}
            options={payment.refundMethods.map((value) => ({
              value,
              label: m.refund.methods[value],
            }))}
            value={method}
            onChange={chooseMethod}
          />
        ) : null}
        <div className={FIELD_ROW}>
          <Field
            className={FIELD}
            label={m.refund.amount}
            hint={format(m.refund.amountHint, { amount: formatEuro(limit, locale) })}
            required
            error={
              amount.trim() !== '' && !amountOk
                ? format(m.refund.amountInvalid, { amount: formatEuro(limit, locale) })
                : null
            }
          >
            {(control) => (
              <StaffInput
                {...control}
                inputMode="decimal"
                autoComplete="off"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            )}
          </Field>
          {byHand ? (
            <Field
              className={FIELD}
              label={m.refund.reference}
              hint={m.refund.referenceHint}
              optionalLabel={m.newBooking.optional}
            >
              {(control) => (
                <StaffInput
                  {...control}
                  maxLength={100}
                  autoComplete="off"
                  value={reference}
                  onChange={(event) => setReference(event.target.value)}
                />
              )}
            </Field>
          ) : null}
        </div>
        <Field
          label={m.refund.reason}
          hint={m.refund.reasonHint}
          required
          error={reason.trim().length > 0 && reason.trim().length < 3 ? m.refund.reasonShort : null}
        >
          {(control) => (
            <StaffInput
              {...control}
              maxLength={500}
              autoComplete="off"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          )}
        </Field>
        {method === 'cash' ? (
          <DrawerField name={`refund-drawer-${code}`} onChange={setCashSessionId} />
        ) : null}
        {byHand ? (
          <FiscalFields
            name={`refund-fiscal-${code}`}
            money="refund"
            draft={fiscalDraft}
            onChange={setFiscalDraft}
            pointFromDrawer={method === 'cash'}
            printed="onRequest"
            missing={documentMissing}
          />
        ) : null}
        {method === 'stripe' ? (
          <p className="text-sm text-text-muted">{m.fiscal.document.stripeLater}</p>
        ) : null}
        {/* The question's refusal, already said in it: in sight here, not said again. */}
        <ProblemNotice problem={problem} quiet={problemSaid} />
        <div className="flex flex-col gap-2">
          <div>
            <Button
              type="submit"
              variant="staff"
              busy={busy}
              disabled={!ready && !busy}
              aria-describedby={ready || busy ? undefined : reasonsId}
            >
              {method === 'stripe' ? m.refund.submitStripe : m.refund.submit}
            </Button>
          </div>
          {busy ? null : <ReasonList id={reasonsId} reasons={reasons} />}
        </div>
      </form>
    </SubPanel>
  )
}

/** "Controlla con Stripe": for a webhook that is slow, or a refund the worker gave up on. */
function SyncWithStripe({ code, onDone }: { code: string; onDone: () => void }) {
  const { messages: m } = useLocale()
  const { takeOver } = useAccess()
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<StaffProblem | null>(null)

  async function sync() {
    setProblem(null)
    setBusy(true)
    const result = await call(
      api.POST('/v1/staff/bookings/{code}/refunds/sync', { params: { path: { code } } }),
    )
    setBusy(false)
    if (result.ok) {
      onDone()
      await refreshAfterChange(queryClient, code)
      return
    }
    if (!takeOver(result.problem)) setProblem(result.problem)
  }

  return (
    <div className="flex flex-col gap-2">
      <div>
        <Button variant="outline" className="rounded-sm" busy={busy} onClick={() => void sync()}>
          {m.refund.sync}
        </Button>
      </div>
      <ProblemNotice problem={problem} />
    </div>
  )
}
